/**
 * Persistent storage adapter.
 *
 * On a Capacitor mobile build, browser `localStorage` lives inside the
 * WebView's data dir. When we ship an app update, the WebView's asset hash
 * changes and Android sometimes resets that store — so users lose their
 * 30-day JWT and have to log in again.
 *
 * This wrapper writes to `@capacitor/preferences` (backed by native Android
 * SharedPreferences) on mobile, which survives any app update. We mirror
 * writes to `localStorage` so non-Capacitor code (incl. axios interceptors,
 * imported helpers, browser tabs) keeps working unchanged.
 *
 * The init() call at app boot also _hydrates_ `localStorage` from Preferences
 * — so a fresh-after-update launch finds the token already present before
 * any auth check runs.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = Capacitor.isNativePlatform();

// Keys we want persisted across app updates.
const PERSIST_KEYS = [
  'access_token',
  'user',
  'company_id',
  'biometric_enabled',
  'last_login_email',
];

export async function init() {
  if (!isNative) return;
  // Hydrate localStorage from Preferences on cold start.
  for (const k of PERSIST_KEYS) {
    try {
      const { value } = await Preferences.get({ key: k });
      if (value !== null && value !== undefined) {
        localStorage.setItem(k, value);
      }
    } catch {
      // ignore — first launch, key doesn't exist yet
    }
  }
}

export async function setItem(key, value) {
  localStorage.setItem(key, value);
  if (isNative && PERSIST_KEYS.includes(key)) {
    try { await Preferences.set({ key, value }); } catch { /* swallow */ }
  }
}

export async function removeItem(key) {
  localStorage.removeItem(key);
  if (isNative && PERSIST_KEYS.includes(key)) {
    try { await Preferences.remove({ key }); } catch { /* swallow */ }
  }
}

// Convenience — clears the ACTIVE SESSION (token + cached user) on logout.
// IMPORTANT: this deliberately KEEPS `biometric_enabled` and `last_login_email`
// so a user who enabled fingerprint unlock can re-login biometrically after an
// explicit logout (the credentials live in the OS keystore, see lib/biometric).
// To fully turn off biometric, call disableBiometric() instead.
export async function clearAuth() {
  for (const k of ['access_token', 'user']) {
    await removeItem(k);
  }
}

// Fully disable biometric unlock: clears the flag here AND deletes the
// credentials from the OS keystore.
export async function disableBiometric() {
  await removeItem('biometric_enabled');
  try {
    const { deleteBiometricCredentials } = await import('./biometric');
    await deleteBiometricCredentials();
  } catch { /* ignore */ }
}
